import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Chip,
    Collapse
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    KeyboardArrowDown as ExpandMoreIcon,
    KeyboardArrowUp as ExpandLessIcon
} from '@mui/icons-material';
import { useNotify, useGetIdentity } from 'react-admin';

interface UserGroup {
    id?: number;
    name: string;
    color: string;
    company: string;
    users?: User[];
}

interface User {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
}

const UserGroupManagement: React.FC = () => {
    const [groups, setGroups] = useState<UserGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null);
    const [formData, setFormData] = useState<UserGroup>({ name: '', color: '#e72956', company: '' });
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [loadingUsers, setLoadingUsers] = useState<Set<number>>(new Set());
    const notify = useNotify();
    const { data: identity } = useGetIdentity();

    const companyId = (identity as any)?.companyId;

    useEffect(() => {
        console.log('[UserGroupManagement] Identity:', identity);
        console.log('[UserGroupManagement] CompanyId:', companyId);
        if (companyId) {
            loadGroups();
        } else {
            setLoading(false);
        }
    }, [companyId]);

    const loadGroups = async () => {
        if (!companyId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_SSO_URL}/api/user_groups?company.id=${companyId}&order[name]=asc`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                console.log('[UserGroupManagement] API Response:', data);
                console.log('[UserGroupManagement] Groups:', data.member || data['hydra:member'] || []);
                setGroups(data.member || data['hydra:member'] || []);
            } else {
                console.error('[UserGroupManagement] API Error:', response.status, response.statusText);
                const errorText = await response.text();
                console.error('[UserGroupManagement] Error details:', errorText);
                notify('Fehler beim Laden der Benutzergruppen', { type: 'error' });
            }
        } catch (error) {
            console.error('Error loading groups:', error);
            notify('Fehler beim Laden der Benutzergruppen', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (group?: UserGroup) => {
        if (group) {
            setEditingGroup(group);
            setFormData(group);
        } else {
            setEditingGroup(null);
            setFormData({ name: '', color: '#e72956', company: `/api/companies/${companyId}` });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingGroup(null);
        setFormData({ name: '', color: '#e72956', company: '' });
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            const url = editingGroup
                ? `${process.env.REACT_APP_SSO_URL}/api/user_groups/${editingGroup.id}`
                : `${process.env.REACT_APP_SSO_URL}/api/user_groups`;

            const method = editingGroup ? 'PUT' : 'POST';

            const dataToSend = editingGroup
                ? formData
                : { ...formData, company: `/api/companies/${companyId}` };

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/ld+json',
                    'Accept': 'application/ld+json'
                },
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                notify(
                    editingGroup
                        ? 'Benutzergruppe erfolgreich aktualisiert'
                        : 'Benutzergruppe erfolgreich erstellt',
                    { type: 'success' }
                );
                handleCloseDialog();
                loadGroups();
            } else {
                notify('Fehler beim Speichern der Benutzergruppe', { type: 'error' });
            }
        } catch (error) {
            console.error('Error saving group:', error);
            notify('Fehler beim Speichern der Benutzergruppe', { type: 'error' });
        }
    };

    const handleDelete = async (groupId: number) => {
        if (!window.confirm('Möchten Sie diese Benutzergruppe wirklich löschen?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_SSO_URL}/api/user_groups/${groupId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                notify('Benutzergruppe erfolgreich gelöscht', { type: 'success' });
                loadGroups();
            } else {
                notify('Fehler beim Löschen der Benutzergruppe', { type: 'error' });
            }
        } catch (error) {
            console.error('Error deleting group:', error);
            notify('Fehler beim Löschen der Benutzergruppe', { type: 'error' });
        }
    };

    const toggleRow = async (groupId: number) => {
        const newExpanded = new Set(expandedRows);

        if (expandedRows.has(groupId)) {
            newExpanded.delete(groupId);
            setExpandedRows(newExpanded);
        } else {
            newExpanded.add(groupId);
            setExpandedRows(newExpanded);

            // Load users for this group if not already loaded
            const group = groups.find(g => g.id === groupId);
            if (group && !group.users) {
                await loadGroupUsers(groupId);
            }
        }
    };

    const loadGroupUsers = async (groupId: number) => {
        setLoadingUsers(prev => new Set(prev).add(groupId));

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_SSO_URL}/api/users?userGroups.id=${groupId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                const users = data.member || data['hydra:member'] || [];

                setGroups(prev => prev.map(grp =>
                    grp.id === groupId ? { ...grp, users } : grp
                ));
            }
        } catch (error) {
            console.error('Error loading group users:', error);
        } finally {
            setLoadingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(groupId);
                return newSet;
            });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Benutzergruppen</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ bgcolor: '#e72956', '&:hover': { bgcolor: '#c21642' } }}
                >
                    Neue Benutzergruppe
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="50"></TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Aktionen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    Keine Benutzergruppen vorhanden
                                </TableCell>
                            </TableRow>
                        ) : (
                            groups.map((group) => (
                                <React.Fragment key={group.id}>
                                    <TableRow>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleRow(group.id!)}
                                            >
                                                {expandedRows.has(group.id!) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{group.name}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(group)}
                                                sx={{ color: '#1f3240' }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(group.id!)}
                                                sx={{ color: '#e72956' }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                                            <Collapse in={expandedRows.has(group.id!)} timeout="auto" unmountOnExit>
                                                <Box sx={{ margin: 2 }}>
                                                    <Typography variant="h6" gutterBottom component="div">
                                                        Zugeordnete Benutzer
                                                    </Typography>
                                                    {loadingUsers.has(group.id!) ? (
                                                        <CircularProgress size={24} />
                                                    ) : group.users && group.users.length > 0 ? (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                            {group.users.map(user => (
                                                                <Chip
                                                                    key={user.id}
                                                                    label={`${user.firstName} ${user.lastName} (${user.email})`}
                                                                    size="small"
                                                                />
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Keine Benutzer zugeordnet
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingGroup ? 'Benutzergruppe bearbeiten' : 'Neue Benutzergruppe'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Abbrechen</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!formData.name}
                        sx={{ bgcolor: '#e72956', '&:hover': { bgcolor: '#c21642' } }}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserGroupManagement;
